import React, { useMemo, useState } from "react";
import { cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";
import TextField from "components/form/textField/TextField";
import { Select } from "components/select/index";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import {
  IWebsiteBuilderEmbedSettings,
  TWebsiteBuilderEmbedProvider,
} from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderEmbed";
import {
  useWebsiteBuilderDebouncedUpdate,
  WebsiteBuilderDebouncedUpdateTime,
} from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import { websiteBuilderUpdateElementSettings } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import {
  IWebsiteBuilderSelectOption,
  renderWebsiteBuilderSelectOptions,
} from "../../../../../helpers/renderWebsiteBuilderSelectOptions";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";

interface IEmbedSettingsProps {
  id: string;
  settings: IWebsiteBuilderEmbedSettings;
  closeSettings: () => void;
}

export function EmbedSettings(props: IEmbedSettingsProps) {
  const { id, settings, closeSettings } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const [updatedSettings, setUpdatedSettings] = useState<IWebsiteBuilderEmbedSettings>(() => cloneDeep(settings));

  const providerOptions: IWebsiteBuilderSelectOption<TWebsiteBuilderEmbedProvider>[] = useMemo(() => {
    return [
      {
        label: t("None"),
        value: "none",
      },
      {
        label: t("Google Calendar"),
        value: "google-calendar",
      },
      {
        label: t("Google Maps"),
        value: "google-maps",
      },
      {
        label: t("YouTube"),
        value: "youtube",
      },
    ];
  }, []);

  function dispatchSettings(prevSettings: IWebsiteBuilderEmbedSettings, nextSettings: IWebsiteBuilderEmbedSettings) {
    dispatch(
      websiteBuilderUpdateElementSettings(activePageId, deviceView, id, {
        id,
        prevElementSettings: {
          type: "embed",
          settings: cloneDeep(prevSettings),
        },
        nextElementSettings: {
          type: "embed",
          settings: cloneDeep(nextSettings),
        },
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: settings,
    updatedState: updatedSettings,
    setUpdatedState: setUpdatedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function renderSettings() {
    return (
      <>
        <Select
          label={t("Provider")}
          defaultValue={updatedSettings.provider}
          onChange={(provider: TWebsiteBuilderEmbedProvider) => {
            if (provider !== updatedSettings.provider) {
              handleUpdateState(prev => ({
                ...prev,
                provider,
                code: "",
              }));
            }
          }}
          usePortal
          portalId={WEBSITE_BUILDER_PORTAL}
        >
          {renderWebsiteBuilderSelectOptions(providerOptions)}
        </Select>

        {updatedSettings.provider !== "none" && (
          <TextField
            label={t("Code")}
            value={updatedSettings.code}
            noResize
            onChange={e =>
              handleDebouncedUpdateState(prev => ({
                ...prev,
                code: e.target.value,
              }))
            }
          />
        )}
      </>
    );
  }

  return (
    <WebsiteBuilderPanelContent
      title={t("Embed settings")}
      closePanel={closeSettings}
      sections={[renderSettings()]}
    />
  );
}
